import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import axios from 'axios';
import { notification } from 'antd';
import { FiPhone, FiX } from 'react-icons/fi';
import { API_BASE_URI } from 'library/constants/api';
import { completeLogin } from 'library/helpers/postLogin';
import { ob } from 'components/Onboarding';

const LOGO_URL =
  'https://bid-your-stay.s3.ap-south-1.amazonaws.com/assets/images/logo/unpaqd_logo.svg';
const DIAL_CODE = '+91';

export interface ListingPreview {
  title?: string;
  location?: string;
  details?: number | null;
  image?: string | null;
}

interface Props {
  preview?: ListingPreview;
  /** Replaces the default line under the title. */
  intro?: string;
  onClose: () => void;
  /** Called once the session is saved; the page then claims the imported listing. */
  onLoggedIn: () => void;
  /** The number isn't registered yet — the page sends them through sign-up and comes back. */
  onNeedSignUp: (phone: string) => void;
}

/** "Log in or sign up" sheet shown once a logged-out visitor's import has finished. */
const SaveListingLoginModal: React.FC<Props> = ({ preview, intro, onClose, onLoggedIn, onNeedSignUp }) => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const phoneReady = /^\d{10}$/.test(phone);
  const otpReady = /^\d{4,6}$/.test(otp);

  const sendOtp = async (): Promise<void> => {
    if (!phoneReady || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_BASE_URI}/auth/requestOtp/${encodeURIComponent(DIAL_CODE + phone)}`,
      );
      const raw = res?.data?.result;
      const result = Array.isArray(raw) ? raw[0] : raw;
      if (result?.user_exist === false) {
        onNeedSignUp(phone);
        return;
      }
      if (res?.data?.responseInformation?.responseCode === 0) {
        setStep('otp');
        notification.success({
          message: 'OTP Sent',
          description: 'Please enter the OTP shared on your WhatsApp.',
        });
      } else {
        setError(
          res?.data?.responseInformation?.responseMessage?.trim() ||
            'Could not send OTP. Please try again in a minute.',
        );
      }
    } catch (e: any) {
      setError(
        e?.response?.data?.responseInformation?.responseMessage?.trim() ||
          'Could not send OTP. Please try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (): Promise<void> => {
    if (!otpReady || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URI}/auth/verifyOtp/`, {
        phoneNo: DIAL_CODE + phone,
        otp,
      });
      const raw = res?.data?.result;
      const result = Array.isArray(raw) ? raw[0] : raw;
      if (res?.data?.responseInformation?.responseCode === 0 && result?.user_exist === false) {
        if (result?.newUserId != null) {
          localStorage.setItem('userDetails', JSON.stringify({ id: result.newUserId }));
        }
        onNeedSignUp(phone);
        return;
      }
      if (
        res?.data?.responseInformation?.responseCode === 0 &&
        result?.jwtToken &&
        result?.userDetails
      ) {
        await completeLogin(result.jwtToken, result.userDetails);
        onLoggedIn();
        return;
      }
      setError('That OTP didn’t work. Please check it and try again.');
    } catch (_) {
      setError('We couldn’t verify the OTP. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (step === 'phone') sendOtp();
    else verifyOtp();
  };

  const detailsLine = [
    preview?.location,
    preview?.details ? `${preview.details} details imported` : null,
  ]
    .filter(Boolean)
    .join(' | ');

  return createPortal(
    <Overlay role="dialog" aria-modal="true" aria-label="Log in or sign up" onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Top>
          <img src={LOGO_URL} alt="Unpaqd" />
          <Close type="button" aria-label="Close" onClick={onClose}>
            <FiX size={22} />
          </Close>
        </Top>

        <h2>Log in or sign up</h2>
        <p className="sub">
          {step === 'phone'
            ? intro || 'Enter your mobile number to continue with Unpaqd.'
            : `Enter the OTP sent to ${DIAL_CODE} ${phone}.`}
        </p>

        {preview?.title ? (
          <Summary>
            {preview.image ? <img src={preview.image} alt="" /> : <span className="ph" />}
            <div>
              <b>Save your listing - {preview.title}</b>
              {detailsLine ? <span>{detailsLine}</span> : null}
            </div>
          </Summary>
        ) : null}

        <form onSubmit={submit}>
          <label htmlFor="save-listing-input">{step === 'phone' ? 'Mobile number' : 'OTP'}</label>
          {step === 'phone' ? (
            <PhoneRow $invalid={!!error}>
              <span className="dial">
                <FiPhone size={18} /> {DIAL_CODE}
              </span>
              <input
                id="save-listing-input"
                ref={inputRef}
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="98765 43210"
                value={phone}
                onChange={(e) => {
                  setError('');
                  setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                }}
              />
            </PhoneRow>
          ) : (
            <PhoneRow $invalid={!!error}>
              <input
                id="save-listing-input"
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => {
                  setError('');
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                }}
              />
            </PhoneRow>
          )}
          {error ? <Err role="alert">{error}</Err> : null}

          <Submit type="submit" disabled={busy || (step === 'phone' ? !phoneReady : !otpReady)}>
            {busy ? 'Please wait…' : step === 'phone' ? 'Continue' : 'Verify & save my listing'}
          </Submit>
          {step === 'otp' ? (
            <Link
              type="button"
              onClick={() => {
                setStep('phone');
                setOtp('');
                setError('');
              }}
            >
              Change number
            </Link>
          ) : null}
        </form>

        <Fine>
          By continuing you agree to Unpaqd&apos;s{' '}
          <a href="/term-of-use" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </Fine>
      </Sheet>
    </Overlay>,
    document.body,
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(26, 26, 26, 0.45);
  backdrop-filter: blur(6px);
  overflow-y: auto;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 26px 26px 22px;
  border-radius: 28px;
  background: #fff;
  box-shadow: ${ob.shadowStrong};
  color: ${ob.text};
  font-family: ${ob.fontBody};
  h2 {
    margin: 22px 0 6px;
    font-family: ${ob.fontDisplay};
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .sub {
    margin: 0 0 18px;
    font-size: 14.5px;
    color: ${ob.muted};
  }
  label {
    display: block;
    margin: 18px 0 8px;
    font-size: 14px;
    font-weight: 600;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  img {
    height: 32px;
  }
`;

const Close = styled.button`
  display: inline-flex;
  padding: 6px;
  border: 0;
  background: transparent;
  color: ${ob.muted};
  cursor: pointer;
  &:hover {
    color: ${ob.text};
  }
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid ${ob.border};
  border-radius: 18px;
  background: ${ob.surface};
  img,
  .ph {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
    background: ${ob.mutedBg};
  }
  div {
    min-width: 0;
  }
  b {
    display: block;
    font-size: 15px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    display: block;
    margin-top: 2px;
    font-size: 12.5px;
    color: ${ob.muted};
  }
`;

const PhoneRow = styled.div<{ $invalid?: boolean }>`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ $invalid }) => ($invalid ? ob.danger : ob.borderStrong)};
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
  &:focus-within {
    border-color: ${ob.primary};
    box-shadow: 0 0 0 3px ${ob.ring};
  }
  .dial {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    border-right: 1px solid ${ob.border};
    font-size: 15px;
    font-weight: 600;
  }
  input {
    flex: 1;
    min-width: 0;
    height: 50px;
    padding: 0 16px;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 16px;
    letter-spacing: 0.02em;
  }
`;

const Err = styled.p`
  margin: 8px 4px 0;
  font-size: 13px;
  color: ${ob.danger};
`;

const Submit = styled.button`
  width: 100%;
  height: 52px;
  margin-top: 20px;
  border: 0;
  border-radius: 999px;
  background: ${ob.primary};
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${ob.shadowBrand};
  transition: opacity 0.15s ease;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Link = styled.button`
  display: block;
  margin: 12px auto 0;
  border: 0;
  background: transparent;
  color: ${ob.muted};
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
`;

const Fine = styled.p`
  margin: 18px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${ob.muted};
  a {
    color: inherit;
    text-decoration: underline;
  }
`;

export default SaveListingLoginModal;
